import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Plane, ArrowRight, ArrowLeft, Check, User, Mail, Clock,
  FileText, MessageSquare, Tag
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { trackEvent } from "@/components/TrackingScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProgressBar({ step }) {
  const { t } = useTranslation();
  const steps = [
    { num: 1, label: t("results.progress_search") },
    { num: 2, label: t("results.progress_info") },
    { num: 3, label: t("results.progress_checkout") },
  ];
  return (
    <div className="flex items-center justify-center gap-0 max-w-md mx-auto" data-testid="progress-bar">
      {steps.map((s, i) => (
        <div key={s.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              s.num < step ? "bg-[#22c55e] text-white" : s.num === step ? "bg-[#f97316] text-white" : "bg-[#e2e8f0] text-[#6b7280]"
            }`}>
              {s.num < step ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className={`text-xs mt-1 font-medium ${s.num <= step ? "text-[#1e3a5f]" : "text-[#6b7280]"}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-14px] ${s.num < step ? "bg-[#22c55e]" : "bg-[#e2e8f0]"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [flight, setFlight] = useState(null);
  const [returnFlight, setReturnFlight] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [travelerCount, setTravelerCount] = useState(1);

  // Multi-passenger state
  const [passengers, setPassengers] = useState([{ title: "Mr", firstName: "", lastName: "" }]);
  const [email, setEmail] = useState("");
  const [validity, setValidity] = useState("48h");
  const [activateLater, setActivateLater] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem("selectedFlight");
    const storedReturn = localStorage.getItem("selectedReturnFlight");
    const storedParams = localStorage.getItem("searchParams");
    if (stored) {
      setFlight(JSON.parse(stored));
      if (storedReturn) setReturnFlight(JSON.parse(storedReturn));
      if (storedParams) {
        const params = JSON.parse(storedParams);
        const count = parseInt(params.travelers) || 1;
        setTravelerCount(count);
        if (count > 1) {
          setPassengers(Array.from({ length: count }, () => ({ title: "Mr", firstName: "", lastName: "" })));
        }
      }
      trackEvent("begin_checkout");
    } else {
      navigate("/");
    }

    fetch(`${API}/pricing`)
      .then((r) => r.json())
      .then((data) => {
        const map = {};
        data.forEach((p) => { map[p.key] = p.value; });
        setPricing(map);
      })
      .catch(() => {});
  }, [navigate]);

  const isRoundTrip = !!returnFlight;

  const subtotal = useMemo(() => {
    if (!pricing) return isRoundTrip ? 17 : 12;
    let total = pricing.base_price_48h || 12;
    if (validity === "7d") total += pricing.addon_7d || 6;
    if (validity === "14d") total += pricing.addon_14d || 14;
    if (activateLater) total += pricing.addon_activate_later || 1;
    if (isRoundTrip) total += pricing.addon_round_trip || 5;
    return total;
  }, [pricing, validity, activateLater, isRoundTrip]);

  const discountAmount = promoResult?.valid ? promoResult.discount_amount : 0;
  const totalPrice = Math.max(0, subtotal - discountAmount);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await fetch(`${API}/promo-codes/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoCode, email: email || "check@temp.com", order_total: subtotal }),
      });
      const data = await res.json();
      setPromoResult(data);
      if (data.valid) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch {
      toast.error("Failed to validate promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  // Re-validate promo when subtotal changes
  useEffect(() => {
    if (promoResult?.valid && promoCode) {
      handleApplyPromo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const updatePassenger = (idx, field, value) => {
    setPassengers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const validate = () => {
    const errs = {};
    passengers.forEach((p, i) => {
      if (!p.firstName || p.firstName.trim().length < 2) errs[`p${i}_firstName`] = `Passenger ${i + 1}: first name required`;
      if (!p.lastName || p.lastName.trim().length < 2) errs[`p${i}_lastName`] = `Passenger ${i + 1}: last name required`;
    });
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = t("checkout.error_email");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validate()) {
      toast.error(t("checkout.fix_errors"));
      return;
    }
    const primary = passengers[0];
    localStorage.setItem("checkoutData", JSON.stringify({
      title: primary.title, firstName: primary.firstName.trim(), lastName: primary.lastName.trim(),
      email: email.trim(), validity, activateLater, notes, promoCode: promoResult?.valid ? promoCode : "", totalPrice,
      discountAmount, subscribeNewsletter, isRoundTrip,
      passengers: passengers.map(p => ({ title: p.title, first_name: p.firstName.trim(), last_name: p.lastName.trim() })),
    }));
    navigate("/checkout/payment");
  };

  if (!flight) return null;

  const validityOptions = [
    { key: "48h", label: "48 hours", addon: 0 },
    { key: "7d", label: "7 days", addon: pricing?.addon_7d || 6 },
    { key: "14d", label: "14 days", addon: pricing?.addon_14d || 14 },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="checkout-page">
      <SEOHead title="Complete Your Booking | OnwardFlighTickets" description="Enter passenger details and choose validity period for your onward flight reservation." path="/checkout" />
      <Header />
      <main className="pt-20">
        <div className="bg-white border-b border-[#e2e8f0] py-5">
          <ProgressBar step={2} />
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* LEFT - Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2"
            >
              <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 md:p-8">
                <h2 className="font-['Outfit'] text-xl font-semibold text-[#1e3a5f] mb-6">
                  {t("checkout.passenger_info")} {travelerCount > 1 && <span className="text-sm font-normal text-[#6b7280]">({travelerCount} travelers)</span>}
                </h2>

                {passengers.map((pax, idx) => (
                  <div key={idx} className={idx > 0 ? "border-t border-[#e2e8f0] pt-5 mt-5" : ""}>
                    <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">
                      Passenger {idx + 1} {idx === 0 && "(Primary)"}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-[#6b7280] mb-1.5">{t("checkout.title_label")} *</label>
                        <select
                          value={pax.title}
                          onChange={(e) => updatePassenger(idx, "title", e.target.value)}
                          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none"
                          data-testid={`title-select-${idx}`}
                        >
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Ms">Ms</option>
                        </select>
                      </div>
                      <div className="sm:col-span-1">
                        <label className="block text-xs font-medium text-[#6b7280] mb-1.5">{t("checkout.first_name")} *</label>
                        <input
                          type="text"
                          value={pax.firstName}
                          onChange={(e) => updatePassenger(idx, "firstName", e.target.value)}
                          className={`w-full border rounded-xl px-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none ${errors[`p${idx}_firstName`] ? "border-red-400" : "border-[#e2e8f0]"}`}
                          placeholder="John"
                          data-testid={`first-name-input-${idx}`}
                        />
                        {errors[`p${idx}_firstName`] && <p className="text-xs text-red-500 mt-1">{errors[`p${idx}_firstName`]}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-[#6b7280] mb-1.5">{t("checkout.last_name")} *</label>
                        <input
                          type="text"
                          value={pax.lastName}
                          onChange={(e) => updatePassenger(idx, "lastName", e.target.value)}
                          className={`w-full border rounded-xl px-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none ${errors[`p${idx}_lastName`] ? "border-red-400" : "border-[#e2e8f0]"}`}
                          placeholder="Doe"
                          data-testid={`last-name-input-${idx}`}
                        />
                        {errors[`p${idx}_lastName`] && <p className="text-xs text-red-500 mt-1">{errors[`p${idx}_lastName`]}</p>}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Email — always from primary passenger */}
                <div className="mb-6 mt-5">
                  <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                    {t("checkout.email_label")} *
                    <span className="text-[#6b7280] font-normal ml-1">({t("checkout.email_hint")})</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full border rounded-xl pl-10 pr-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none ${errors.email ? "border-red-400" : "border-[#e2e8f0]"}`}
                      placeholder="your@email.com"
                      data-testid="email-input"
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                {/* Validity selector */}
                <div className="mb-6">
                  <label className="block text-xs font-medium text-[#6b7280] mb-3">{t("checkout.ticket_valid")}</label>
                  <div className="flex gap-2" data-testid="validity-selector">
                    {validityOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setValidity(opt.key)}
                        className={`flex-1 py-3 px-3 rounded-xl text-sm font-medium border-2 transition-all ${
                          validity === opt.key
                            ? "border-[#f97316] bg-[#fff7ed] text-[#f97316]"
                            : "border-[#e2e8f0] bg-white text-[#1f2937] hover:border-[#d1d5db]"
                        }`}
                        data-testid={`validity-${opt.key}`}
                      >
                        {opt.label}
                        {opt.addon > 0 && <span className="block text-xs mt-0.5 text-[#6b7280]">+${opt.addon}</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Add-ons */}
                <div className="space-y-3 mb-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={activateLater}
                      onChange={(e) => setActivateLater(e.target.checked)}
                      className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
                      data-testid="activate-later-checkbox"
                    />
                    <span className="text-sm text-[#1f2937] group-hover:text-[#f97316]">
                      {t("checkout.activate_later")} <span className="text-[#f97316] font-medium">(+${pricing?.addon_activate_later || 1})</span>
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={showNotes}
                      onChange={(e) => setShowNotes(e.target.checked)}
                      className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
                      data-testid="notes-checkbox"
                    />
                    <span className="text-sm text-[#1f2937] group-hover:text-[#f97316]">{t("checkout.add_notes")}</span>
                  </label>
                  {showNotes && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none resize-none h-20"
                        placeholder={t("checkout.notes_placeholder")}
                        data-testid="notes-textarea"
                      />
                    </motion.div>
                  )}

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={showPromo}
                      onChange={(e) => setShowPromo(e.target.checked)}
                      className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
                      data-testid="promo-checkbox"
                    />
                    <span className="text-sm text-[#1f2937] group-hover:text-[#f97316]">{t("checkout.promo_code")}</span>
                  </label>
                  {showPromo && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                            className="w-full border border-[#e2e8f0] rounded-xl pl-10 pr-4 py-3 text-sm bg-[#f8fafc] text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none uppercase"
                            placeholder="PROMO123"
                            data-testid="promo-input"
                          />
                        </div>
                        <button
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoCode.trim()}
                          className="px-5 py-3 bg-[#1e3a5f] text-white rounded-xl text-sm font-medium hover:bg-[#152e4d] transition-colors disabled:opacity-50"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? "..." : "Apply"}
                        </button>
                      </div>
                      {promoResult && (
                        <p className={`text-xs mt-2 font-medium ${promoResult.valid ? "text-[#22c55e]" : "text-red-500"}`} data-testid="promo-result">
                          {promoResult.message}
                          {promoResult.valid && ` (-$${promoResult.discount_amount.toFixed(2)})`}
                        </p>
                      )}
                    </motion.div>
                  )}

                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={subscribeNewsletter}
                      onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                      className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
                      data-testid="newsletter-checkbox"
                    />
                    <span className="text-sm text-[#6b7280] group-hover:text-[#1f2937]">
                      Subscribe to travel deals and tips (you can unsubscribe anytime)
                    </span>
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-[#6b7280] hover:text-[#1f2937] font-medium transition-colors"
                    data-testid="previous-btn"
                  >
                    <ArrowLeft className="w-4 h-4" /> {t("checkout.previous")}
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex-1 bg-[#f97316] text-white py-3.5 rounded-xl font-semibold text-base hover:bg-[#ea580c] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
                    data-testid="next-step-btn"
                  >
                    {t("checkout.next_step")} (${totalPrice.toFixed(2)})
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* RIGHT - Flight Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 sticky top-24" data-testid="flight-summary-card">
                {/* Outbound */}
                <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">
                  {isRoundTrip ? "Outbound" : "Flight"}
                </p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{flight.departure_code}</span>
                  <div className="flex-1 flex items-center mx-3">
                    <div className="flex-1 h-px bg-[#e2e8f0]" />
                    <Plane className="w-4 h-4 text-[#f97316] mx-1 -rotate-45" />
                    <div className="flex-1 h-px bg-[#e2e8f0]" />
                  </div>
                  <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{flight.arrival_code}</span>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-[#6b7280]">{t("checkout.depart")}</span>
                    <span className="text-[#1f2937] font-medium">{flight.departure_date} {flight.departure_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6b7280]">{t("checkout.airline")}</span>
                    <span className="text-[#1f2937] font-medium">{flight.airline} {flight.flight_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6b7280]">{t("checkout.duration")}</span>
                    <span className="text-[#1f2937] font-medium">{flight.duration_text}</span>
                  </div>
                </div>

                {/* Return Flight */}
                {isRoundTrip && returnFlight && (
                  <>
                    <div className="border-t border-[#e2e8f0] my-4" />
                    <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">Return</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{returnFlight.departure_code}</span>
                      <div className="flex-1 flex items-center mx-3">
                        <div className="flex-1 h-px bg-[#e2e8f0]" />
                        <Plane className="w-4 h-4 text-[#f97316] mx-1 -rotate-45" />
                        <div className="flex-1 h-px bg-[#e2e8f0]" />
                      </div>
                      <span className="text-lg font-bold text-[#1e3a5f] font-['Outfit']">{returnFlight.arrival_code}</span>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-[#6b7280]">{t("checkout.depart")}</span>
                        <span className="text-[#1f2937] font-medium">{returnFlight.departure_date} {returnFlight.departure_time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#6b7280]">{t("checkout.airline")}</span>
                        <span className="text-[#1f2937] font-medium">{returnFlight.airline} {returnFlight.flight_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#6b7280]">{t("checkout.duration")}</span>
                        <span className="text-[#1f2937] font-medium">{returnFlight.duration_text}</span>
                      </div>
                    </div>
                  </>
                )}

                <div className="border-t border-[#e2e8f0] pt-4">
                  {isRoundTrip && (
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#6b7280]">Round trip addon</span>
                      <span className="text-[#1f2937]">+${pricing?.addon_round_trip || 5}</span>
                    </div>
                  )}
                  {validity !== "48h" && (
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#6b7280]">{t("checkout.validity_addon")}</span>
                      <span className="text-[#1f2937]">+${validity === "7d" ? (pricing?.addon_7d || 6) : (pricing?.addon_14d || 14)}</span>
                    </div>
                  )}
                  {activateLater && (
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#6b7280]">{t("checkout.activate_later_label")}</span>
                      <span className="text-[#1f2937]">+${pricing?.addon_activate_later || 1}</span>
                    </div>
                  )}
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#22c55e] font-medium">Promo ({promoCode})</span>
                      <span className="text-[#22c55e] font-medium">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline mt-3">
                    <span className="text-sm font-semibold text-[#1e3a5f]">{t("checkout.total")}</span>
                    <span className="text-2xl font-bold text-[#f97316] font-['Outfit']" data-testid="total-price">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
